import React, { useState, KeyboardEvent, useEffect } from 'react'
import { X } from 'lucide-react'
import {
  FaEnvelope,
  FaSms,
  FaTelegramPlane,
  FaVoicemail,
  FaWeebly,
  FaWhatsapp,
} from "react-icons/fa";
import { RiMessage2Line } from "react-icons/ri";
import { IconType } from "react-icons";
import { TemplateVariables } from '../../../views/marketing-pages/create-campaign/types'
import { CampaignData } from '../../types'
import { SingleCampaignResponse, WalletBalanceData } from '../../../views/marketing-pages/campaign/types'
import useRequest from '../../hook/use-req'
import { toast } from 'sonner'
import { CostEstimateResponse } from './sms/types';
import { LivePreviewPanel } from './sms/live-preview';
import { CustomDateTimePicker } from '../../date-picker';
import { TestEmailModal } from './test-email';
import { TestPhoneModal } from './test-phone';
import { CostBreakdownModal } from './breakdown-pop';
import useApi from '../../hook/req';
import AddFundsModal from '../wallet/funds-modal';
import { apiErrorMessage } from '../../../utils/function';


// ─── Types ────────────────────────────────────────────────────────────────────

type ScheduleMode = 'now' | 'later'

export interface ScheduleLaunchFormData {
  tags: string[]
  scheduleMode: ScheduleMode
  sendTime: string
  timeZone: string
  launch: boolean
}

interface ScheduleLaunchProps {
  campaignData: CampaignData;
  walletBallance: WalletBalanceData | null;
  singleCampaign: SingleCampaignResponse | null;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onSubmit: (data: Partial<CampaignData>) => void;
  onBack: () => void;
  // Called after a successful top-up so the parent can refetch
  // /wallet/balance — same request the Wallet page itself uses — and this
  // screen's insufficientBalance check reflects the new balance right away.
  onWalletFunded?: () => void;
}
// ─── Channel Icons ────────────────────────────────────────────────────────────

const ChannelIcon: React.FC<{ channel: string }> = ({ channel }) => {
  const getIcon = (): IconType => {
    switch (channel?.toLowerCase()) {
      case 'whatsapp':
        return FaWhatsapp;
      case 'sms':
        return FaSms;
      case 'email':
        return FaEnvelope;
      case 'voice':
        return FaVoicemail;
      case 'rcs':
        return RiMessage2Line;
      case 'webchat':
        return FaWeebly;
      case 'telegram':
        return FaTelegramPlane;
      default:
        return FaSms;
    }
  };

  const Icon = getIcon() as React.ComponentType<{ className?: string }>;

  return (
    <span className={`flex items-center justify-center text-[#72C078] rounded-full shrink-0`}>
      <Icon className='w-5 h-5' />
    </span>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

interface InfoRowProps {
  items: Array<{
    label: string;
    value: React.ReactNode;
  }>;
}

const InfoRow: React.FC<InfoRowProps> = ({ items }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
    {items.map((item, index) => (
      <div key={index}>
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
          {item.label}
        </p>
        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {item.value}
        </div>
      </div>
    ))}
  </div>
);

const Divider = () => <div className="border-t border-gray-100 dark:border-gray-800" />

// ─── BroadcastTags ────────────────────────────────────────────────────────────

interface BroadcastTagsProps {
  tags: string[]
  onChange: (tags: string[]) => void
}

const BroadcastTags: React.FC<BroadcastTagsProps> = ({ tags, onChange }) => {
  const [input, setInput] = useState('')

  const addTag = (value: string) => {
    const trimmed = value.trim().replace(/^#*/, '')
    if (trimmed && !tags.includes(`#${trimmed}`)) {
      onChange([...tags, `#${trimmed}`])
    }
    setInput('')
  }

  const removeTag = (tag: string) => onChange(tags.filter((t) => t !== tag))

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag(input)
    } else if (e.key === 'Backspace' && input === '' && tags.length > 0) {
      onChange(tags.slice(0, -1))
    }
  }

  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
        Broadcast Tags
      </p>
      <div
        className="flex flex-wrap gap-2 items-center min-h-[44px] w-full rounded-lg border-2 border-violet-400 bg-white dark:bg-gray-800 px-3 py-2 cursor-text focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-100 transition-all"
        onClick={() => document.getElementById('tag-input')?.focus()}
      >
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-md bg-violet-50 border border-violet-200 text-violet-700 text-xs font-semibold px-2 py-1"
          >
            {tag}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(tag) }}
              className="text-violet-400 hover:text-violet-700 transition-colors"
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          id="tag-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => input && addTag(input)}
          placeholder={tags.length === 0 ? 'Add tags...' : ''}
          className="flex-1 min-w-[80px] outline-none text-sm text-gray-700 dark:text-gray-300 bg-transparent placeholder-gray-400"
        />
      </div>
      <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Press Enter or comma to add a tag</p>
    </div>
  )
}

// ─── ScheduleOptions ──────────────────────────────────────────────────────────

interface ScheduleOptionsProps {
  mode: ScheduleMode
  onModeChange: (mode: ScheduleMode) => void
  selectedDateTime: string
  onDateTimeChange: (value: string) => void
  timeZone: string
  onTimeZoneChange: (value: string) => void
}

// "YYYY-MM-DDTHH:mm" in the browser's own local time — the same shape
// `selectedDateTime` is parsed back from with `new Date(...)` (which reads a
// zone-less string as local). toISOString() can't be used for this: it's UTC,
// so reading it back as local shifted the time by the UTC offset.
const toLocalDateTimeValue = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ScheduleOptions: React.FC<ScheduleOptionsProps> = ({
  mode, onModeChange, selectedDateTime, onDateTimeChange, timeZone, onTimeZoneChange,
}) => {
  // The picker writes straight into the parent's `selectedDateTime` — it used
  // to live in a private useForm here that nothing ever read, so whatever was
  // picked, the launch payload still carried the initial "tomorrow 9:00"
  // default (and, via the UTC bug above, tomorrow 08:00 in Lagos).
  const pickerValue = React.useMemo(() => (selectedDateTime ? new Date(selectedDateTime) : null), [selectedDateTime]);

  return (
    <div className="space-y-4">
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Schedule</p>

      <div className="grid grid-cols-2 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <button
          type="button"
          onClick={() => onModeChange('now')}
          className={`py-2.5 text-sm font-semibold transition-all ${mode === 'now' ? 'bg-white dark:bg-gray-700 text-[#6366F1] shadow-sm' : 'text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
            }`}
        >
          Send Now
        </button>
        <button
          type="button"
          onClick={() => onModeChange('later')}
          className={`py-2.5 text-sm font-semibold transition-all ${mode === 'later' ? 'bg-[#fff] dark:bg-gray-700 text-[#6366F1] shadow-sm' : 'text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
            }`}
        >
          Schedule Later
        </button>
      </div>

      {mode === 'later' && (
        <div className="w-full sm:w-[70%] gap-4">
          <div className='w-full'>
            <div className="h-[50px] w-full">
              <CustomDateTimePicker
                value={pickerValue}
                onChange={(date) => onDateTimeChange(date ? toLocalDateTimeValue(date) : '')}
                error={selectedDateTime ? undefined : "Start date and time is required"}
                label="Start Date & Time"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── FormActions ──────────────────────────────────────────────────────────────

interface FormActionsProps {
  onBack: () => void;
  onSaveAsDraft: () => void;
  onScheduleAndLaunch: () => void;
  onSendTestEmail: () => void;
  onSendTestPhone: () => void;
  isLoading?: boolean;
  isLoader?: boolean;
  hasSentTestEmail: boolean;
  hasSentTestPhone: boolean;
  isEmailChannel: boolean;
  // Only whatsapp/sms get a phone-based test send — email has its own,
  // and rcs/voice/telegram aren't wired to a test-send endpoint yet.
  isTestablePhoneChannel: boolean;
  phoneChannelLabel: string;
  // True only once both the wallet balance and a real cost estimate have
  // loaded and the balance genuinely can't cover it — see the computation
  // next to Wallet Balance/Total Cost above where this component is used.
  insufficientBalance?: boolean;
}

const FormActions: React.FC<FormActionsProps> = ({
  onBack,
  onSaveAsDraft,
  onScheduleAndLaunch,
  onSendTestEmail,
  onSendTestPhone,
  isLoading,
  isLoader = false,
  hasSentTestEmail,
  hasSentTestPhone,
  isEmailChannel,
  isTestablePhoneChannel,
  phoneChannelLabel,
  insufficientBalance,
}) => {
  const requiresTest = isEmailChannel || isTestablePhoneChannel;
  const testSatisfied = isEmailChannel ? hasSentTestEmail : hasSentTestPhone;

  return (
    <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
      <button
        type="button"
        onClick={onSaveAsDraft}
        className="text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors py-2 px-1 text-center sm:text-left"
      >
        {isLoader ? 'Drafting...' : 'Save as Draft'}
      </button>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 sm:flex-none px-6 py-2.5 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          Back
        </button>

        {/* Show Send Test Email button for email channel only */}
        {isEmailChannel && !hasSentTestEmail && (
          <button
            type="button"
            onClick={onSendTestEmail}
            className="flex-1 sm:flex-none px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-sm"
          >
            Send Test Email
          </button>
        )}

        {/* Show Send Test WhatsApp/SMS button for those channels only */}
        {isTestablePhoneChannel && !hasSentTestPhone && (
          <button
            type="button"
            onClick={onSendTestPhone}
            className="flex-1 sm:flex-none px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-sm"
          >
            Send Test {phoneChannelLabel}
          </button>
        )}

        {/* Show Schedule and Launch button only after the required test send is done */}
        {(!requiresTest || testSatisfied) && (
          <div className="flex-1 sm:flex-none flex flex-col items-end gap-1">
            <button
              type="button"
              onClick={onScheduleAndLaunch}
              disabled={isLoading || insufficientBalance}
              title={insufficientBalance ? "Your wallet balance is lower than the estimated cost of this broadcast" : undefined}
              className="w-full sm:w-auto px-6 py-2.5 rounded-lg text-sm font-bold text-white bg-[#4338CA] hover:bg-violet-700 active:bg-violet-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
            >
              {isLoading ? 'Launching...' : 'Schedule and Launch'}
            </button>
            {insufficientBalance && (
              <span className="text-xs text-red-600 dark:text-red-400 text-right">
                Insufficient wallet balance — top up to launch
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const ScheduleLaunch: React.FC<ScheduleLaunchProps> = ({
  campaignData,
  walletBallance,
  singleCampaign,
  onSubmit,
  onBack,
  onWalletFunded,
}) => {
  const token = localStorage.getItem("authToken");
  const campaignId = localStorage.getItem("campaignId");
  const audienceId = localStorage.getItem("audienceId");
  const [isExpanded, setIsExpanded] = useState(true);
  // Same placeholder used by the Wallet page's own AddFundsModal usage —
  // there's no real logged-in-user email stored anywhere in this app yet.
  const [userEmail] = useState("user@example.com");
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [showTestEmail, setShowTestEmail] = useState(false);
  const [hasSentTestEmail, setHasSentTestEmail] = useState(false);
  const [showTestPhone, setShowTestPhone] = useState(false);
  const [hasSentTestPhone, setHasSentTestPhone] = useState(false);

  const [tags, setTags] = useState<string[]>(() => {
    return campaignData.tags || [];
  });

  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('now');
  const [costEstimate, setCostEstimate] = useState<CostEstimateResponse | null>(null);
  const { makeRequest: getEstimate } = useRequest(`/campaign/estimate_cost`, "GET",
    { Authorization: `Bearer ${token}` }
  );

  // Use useApi for form data uploads
  const { makeRequest: launchCampaignFormData, loading } = useApi("/campaign/update", "PUT", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: updateCampaignFormData, loading: loader } = useApi("/campaign/update", "PUT", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: sendTestEmail } = useRequest(`/campaign/${campaignData?.campaign_id || campaignId}/preview-email`, "POST", {
    Authorization: `Bearer ${token}`,
  });

  // Mirrors the email test-send endpoint's naming (`preview-email`) —
  // unconfirmed against real backend docs for whatsapp/sms specifically,
  // matching this repo's established `/campaign/{id}/preview-{channel}`
  // pattern. Flag to backend if either 404s.
  const { makeRequest: sendTestPhone } = useRequest(
    `/campaign/${campaignData?.campaign_id || campaignId}/preview-${(campaignData.campaign_channel?.toLowerCase() || 'whatsapp')}`,
    "POST",
    { Authorization: `Bearer ${token}` }
  );

  const [selectedDateTime, setSelectedDateTime] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    return toLocalDateTimeValue(tomorrow);
  });

  const [timeZone, setTimeZone] = useState('Africa/Lagos');

  // Check if channel is email
  const isEmailChannel = campaignData.campaign_channel?.toLowerCase() === 'email';
  const lowerChannel = campaignData.campaign_channel?.toLowerCase() || '';
  const isTestablePhoneChannel = lowerChannel === 'whatsapp' || lowerChannel === 'sms';
  const phoneChannelLabel = lowerChannel === 'sms' ? 'SMS' : 'WhatsApp';

  // Format data from campaignData
  const broadcastName = campaignData.campaign_name || singleCampaign?.campaign_name || 'Untitled Campaign';
  const channel = campaignData.campaign_channel || singleCampaign?.campaign_channel || 'WhatsApp';
  const senderProfile = campaignData.sender_name || singleCampaign?.sender_name || '+123 - 9900990099';

  const audienceSize = costEstimate?.campaign_size || 0;
  const estimatedCost = costEstimate?.estimated_cost;

  // Only blocks launch once both the wallet balance and a real cost
  // estimate have actually loaded — a still-null wallet or unset
  // estimatedCost must not disable the button before that data arrives.
  const insufficientBalance =
    walletBallance != null && estimatedCost != null && walletBallance.balance < estimatedCost;
  const estimatedReach = `${audienceSize} Contacts`;
  // Was `${costEstimate?.currency_symbol}` — while costEstimate is still
  // loading (null), that template literal stringified to the literal text
  // "undefined" and showed up next to the cost/wallet numbers.
  const ActualCurrency = costEstimate?.currency_symbol || "";
  const subscribedContacts = costEstimate?.subscribed_contacts;
  const unsubscribedContacts = costEstimate?.unsubscribed_contacts;
  const [showCostBreakdown, setShowCostBreakdown] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!campaignId) return;

      const campaignPayload = {
        campaign_id: campaignId,
        channel: campaignData?.campaign_channel,
        message: campaignData?.messages,
        audience_ids: audienceId || campaignData?.audienceId,
        sms_segments: campaignData?.smsSegment
      };

      const [response] = await getEstimate(undefined, campaignPayload);
      setCostEstimate(response);
    };

    fetchData();
  }, [campaignId]);

  const handleSendTestEmail = async (recipients: string[]) => {
    const contentToSend = isEmailChannel
      ? (campaignData?.html_text || campaignData?.messages || "")
      : (campaignData?.messages || "");

    if (!contentToSend.trim()) {
      toast.error("Please add email content before sending a test email");
      return;
    }

    if (!campaignData.subject?.trim()) {
      toast.error("Please add a subject before sending a test email");
      return;
    }

    let contentObject: {
      html_text: string;
      subject: string;
      default_variables?: Array<{ name: string; defaultValue: string }>;
    } = {
      html_text: contentToSend,
      subject: campaignData.subject?.trim() || ""
    };

    const variableRegex = /{{(.*?)}}/g;
    const matches = contentObject.html_text.match(variableRegex);
    if (matches && matches.length > 0) {
      const variables = new Set<string>();
      matches.forEach(match => {
        const varName = match.replace(/{{|}}/g, "").trim();
        variables.add(varName);
      });

      contentObject.default_variables = Array.from(variables).map(name => ({
        name: name,
        defaultValue: `[${name}]`
      }));
    }

    const payload = {
      to: recipients,
      content: contentObject
    };

    const [response, status] = await sendTestEmail(payload);

    if (status === 200 || response?.status === true) {
      toast.success(`Test email sent successfully to ${recipients.length} recipient(s)!`);
      setHasSentTestEmail(true);
      setShowTestEmail(false);
    } else {
      toast.error(apiErrorMessage(response, "Failed to send test email"));
    }
  };

  const handleOpenTestPhoneModal = () => {
    setShowTestPhone(true);
  };

  // Content shape mirrors the real WhatsApp/SMS launch payload built in
  // buildFormDataPayload's "else" branch below — media isn't included here
  // since this goes as a plain JSON POST (matching preview-email's shape)
  // rather than FormData; a template using a media header won't preview
  // that image/video on a test send yet.
  const handleSendTestPhone = async (recipients: string[]) => {
    if (!campaignData?.messages?.trim() && !campaignData.content?.template_id) {
      toast.error(`Please add ${phoneChannelLabel} content before sending a test message`);
      return;
    }

    const contentObject = {
      template_id: campaignData.content?.template_id || "",
      template_name: campaignData.content?.template_name || "",
      variables: campaignData.content?.variables || {},
      header_type: campaignData.content?.header_type || "none",
      message: campaignData.messages || "",
      footer: campaignData.content?.footer || "Reply STOP to unsubscribe",
      buttons: campaignData.link ? [{
        type: "URL",
        text: "Learn More",
        url: campaignData.link
      }] : (campaignData.content?.buttons || []),
    };

    const payload = {
      to: recipients,
      content: contentObject,
    };

    const [response, status] = await sendTestPhone(payload);

    if (status === 200 || response?.status === true) {
      toast.success(`Test ${phoneChannelLabel} sent successfully to ${recipients.length} recipient(s)!`);
      setHasSentTestPhone(true);
      setShowTestPhone(false);
    } else {
      toast.error(apiErrorMessage(response, `Failed to send test ${phoneChannelLabel}`));
    }
  };

  // Helper function to build content payload with FormData support


  const buildFormDataPayload = (isLaunch: boolean = true) => {
    const formData = new FormData();

    let scheduledStartTimestamp = null;
    let sendNow = true;

    if (scheduleMode === 'later' && selectedDateTime) {
      const localDateTime = new Date(selectedDateTime);
      scheduledStartTimestamp = Math.floor(localDateTime.getTime() / 1000);
      sendNow = false;
    }

    // Get all audience IDs
    const allAudienceIds = [
      ...(campaignData.selectedAudiences?.map(a => a.audience_id) || []),
      campaignData.uploadAudienceId,
      campaignData.manualAudienceId
    ].filter(Boolean) as string[];

    // Append common fields
    formData.append('campaign_id', campaignId || '');
    formData.append('audience_ids', JSON.stringify(allAudienceIds));
    formData.append('tags', JSON.stringify(tags.map(tag => tag.replace('#', ''))));
    formData.append('send_now', String(sendNow));

    if (scheduledStartTimestamp) {
      formData.append('scheduled_start', String(scheduledStartTimestamp));
    }

    // Build content object based on channel
    const isEmailChannel = campaignData.campaign_channel?.toLowerCase() === 'email';
    const isRcsChannel = campaignData.campaign_channel?.toLowerCase() === 'rcs';

    let contentPayload: any = {};

    if (isEmailChannel) {
      // Email content
      contentPayload = {
        template_id: campaignData?.template_id || null,
        personalize: true,
        data_to_personalize: campaignData.content?.data_to_personalize || [],
        variable_column_map: campaignData.content?.variable_column_map || {},
        subject: campaignData.subject || "",
        email_header: campaignData.email_header || "",
        html_text: campaignData.html_text || "",
        plain_text: campaignData.plain_text || "",
      };
      formData.append('campaign_channel', 'EMAIL');
    } else if (isRcsChannel) {
      // RCS content
      contentPayload = {
        template_id: campaignData.content?.template_id || "",
        template_name: campaignData.content?.template_name || "",
        variables: campaignData.content?.variables || {},
        variable_column_map: campaignData.content?.variable_column_map || {},
        data_to_personalize: campaignData.content?.data_to_personalize || [],
        personalize: campaignData.content?.personalize || false,
        message: campaignData.messages || "",
      };
      formData.append('campaign_channel', 'RCS');
    } else {
      // WhatsApp / SMS content
      contentPayload = {
        template_id: campaignData.content?.template_id || "",
        template_name: campaignData.content?.template_name || "",
        variables: campaignData.content?.variables || {},
        variable_column_map: campaignData.content?.variable_column_map || {},   // ← Added
        data_to_personalize: campaignData.content?.data_to_personalize || [],   // ← Added
        personalize: campaignData.content?.personalize || false,
        header_type: campaignData.content?.header_type || "none",
        message: campaignData.messages || "",
        footer: campaignData.content?.footer || "Reply STOP to unsubscribe",
        buttons: campaignData.link ? [{
          type: "URL",
          text: "Learn More",
          url: campaignData.link
        }] : (campaignData.content?.buttons || [])
      };

      // Handle media file
      if (campaignData.mediaFile) {
        formData.append('media_file', campaignData.mediaFile);
        formData.append('media_file_name', campaignData.mediaFile.name);
        formData.append('media_file_type', campaignData.mediaFile.type);

        contentPayload.header_value = campaignData.mediaFile.name;
        contentPayload.header_mime_type = campaignData.mediaFile.type;
      } else if (campaignData.content?.header_value && !campaignData.content?.header_value.startsWith('blob:')) {
        contentPayload.header_value = campaignData.content.header_value;
      }

      if (campaignData.mediaFileUrl && !campaignData.mediaFileUrl.startsWith('blob:')) {
        contentPayload.media_url = campaignData.mediaFileUrl;
      }

      formData.append('campaign_channel', campaignData.campaign_channel || 'WHATSAPP');
    }

    // Append the final content object
    formData.append('content', JSON.stringify(contentPayload));

    if (estimatedCost) formData.append('estimated_cost', String(estimatedCost));
    if (audienceSize) formData.append('campaign_size', String(audienceSize));
    formData.append('dnd', 'false');
    formData.append('launch', String(isLaunch));

    return formData;
  };

  const handleLaunch = async () => {
    try {
      const formData = buildFormDataPayload(true);

      // Log form data contents for debugging
     
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
         
        } else {
         
        }
      }

      const [response] = await launchCampaignFormData(formData);

      if (response?.status) {
        toast.success(response?.message || "Campaign launched successfully!");

        onSubmit({
          ...campaignData,
          scheduled_start: scheduleMode === 'later' && selectedDateTime ? new Date(selectedDateTime).toISOString() : null,
          tags: tags,
          send_now: scheduleMode === 'now'
        });

        // Clear localStorage
        localStorage.removeItem("draftCampaignId");
        localStorage.removeItem("draftAudienceId");
        localStorage.removeItem("campaignId");
        localStorage.removeItem("audienceId");
        localStorage.removeItem("csv_headers");
        localStorage.removeItem("metadata");
        localStorage.removeItem("first_row_values");

      } else {
        toast.error(apiErrorMessage(response, "Failed to launch campaign"));
      }
    } catch (error: any) {
      console.error("Error launching campaign:", error);
      toast.error(error?.message || "An error occurred while launching the campaign");
    }
  };

  const handleSaveAsDraft = async () => {
    try {
      const formData = buildFormDataPayload(false);

      const [response] = await updateCampaignFormData(formData);

      if (response?.status) {
        toast.success(response?.message || "Campaign saved as draft successfully!");

        onSubmit({
          ...campaignData,
          scheduled_start: scheduleMode === 'later' && selectedDateTime ? new Date(selectedDateTime).toISOString() : null,
          tags: tags,
          send_now: scheduleMode === 'now'
        });

        // Clear localStorage
        localStorage.removeItem("draftCampaignId");
        localStorage.removeItem("draftAudienceId");
        localStorage.removeItem("campaignId");
        localStorage.removeItem("audienceId");
        localStorage.removeItem("csv_headers");
        localStorage.removeItem("metadata");
        localStorage.removeItem("first_row_values");
      } else {
        toast.error(apiErrorMessage(response, "Failed to save campaign as draft"));
      }
    } catch (error: any) {
      console.error("Error saving draft:", error);
      toast.error(error?.message || "An error occurred while saving the draft");
    }
  };

  const handleOpenTestEmailModal = () => {
    setShowTestEmail(true);
  };

  return (
    <div className="min-h-screen flex items-start justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── Form panel ── */}
          <div className="flex-1 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 sm:p-8 space-y-8">
            {/* Header */}
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">Schedule and Launch</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                We have pre-filled this based on your brief. You may edit anything or proceed.
              </p>
            </div>

            <Divider />

            {/* Broadcast info rows */}
            <div className="space-y-6">
              <InfoRow
                items={[
                  {
                    label: 'Channels',
                    value: (
                      <div className="flex items-center gap-2">
                        <ChannelIcon channel={channel} />
                        <span className="capitalize">{channel}</span>
                      </div>
                    ),
                  },
                  {
                    label: 'Broadcast Name',
                    value: broadcastName,
                  },
                  {
                    label: 'Sender Profile',
                    value: senderProfile,
                  },
                ]}
              />

              <Divider />
              <InfoRow
                items={[
                  {
                    label: 'Estimated Reach',
                    value: estimatedReach || "0 Contacts",
                  },
                  {
                    label: 'Wallet Balance',
                    value: `${ActualCurrency} ${walletBallance?.balance || 0}`,
                  },
                  {
                    label: 'Total Cost',
                    value: `${ActualCurrency} ${estimatedCost || 0}`,
                  },
                ]}
              />

              {insufficientBalance && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-800 rounded-xl p-3">
                  <p className="flex-1 text-sm text-red-700 dark:text-red-400">
                    Your wallet balance ({ActualCurrency} {walletBallance?.balance || 0}) is lower than the estimated
                    cost ({ActualCurrency} {estimatedCost || 0}) — top up your wallet to launch this broadcast.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowAddFundsModal(true)}
                    className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm shrink-0 self-start sm:self-auto"
                  >
                    Add Funds
                  </button>
                </div>
              )}

              {(subscribedContacts != null || unsubscribedContacts != null) && (
                <InfoRow
                  items={[
                   
                    {
                      label: 'Unsubscribed Contacts',
                      value: unsubscribedContacts ?? 0,
                    },
                  ]}
                />
              )}
              <div className="flex w-full sm:w-[77%] justify-end items-end">
                {(campaignData?.campaign_channel?.toUpperCase() === "WHATSAPP" ||
                  campaignData?.campaign_channel?.toUpperCase() === "SMS" ||
                  campaignData?.campaign_channel?.toUpperCase() === "RCS") && (
                    <button
                      onClick={() => setShowCostBreakdown(true)}
                      className="text-base text-[#4338CA] underline font-semibold hover:text-indigo-700 transition-colors"
                    >
                      View full breakdown
                    </button>
                  )}
              </div>

            </div>

            <Divider />

            <BroadcastTags tags={tags} onChange={setTags} />

            <Divider />

            <ScheduleOptions
              mode={scheduleMode}
              onModeChange={setScheduleMode}
              selectedDateTime={selectedDateTime}
              onDateTimeChange={setSelectedDateTime}
              timeZone={timeZone}
              onTimeZoneChange={setTimeZone}
            />

            <Divider />

            <FormActions
              onBack={onBack}
              onSaveAsDraft={handleSaveAsDraft}
              onScheduleAndLaunch={handleLaunch}
              onSendTestEmail={handleOpenTestEmailModal}
              onSendTestPhone={handleOpenTestPhoneModal}
              isLoading={loading}
              isLoader={loader}
              hasSentTestEmail={hasSentTestEmail}
              hasSentTestPhone={hasSentTestPhone}
              isEmailChannel={isEmailChannel}
              isTestablePhoneChannel={isTestablePhoneChannel}
              phoneChannelLabel={phoneChannelLabel}
              insufficientBalance={insufficientBalance}
            />
          </div>

          {/* ── Live Preview panel ── */}
          <div className="w-full lg:w-80 xl:w-96">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 h-full">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                Live Preview
              </p>
              <div className="flex items-center justify-center">
                {/* campaignData.link is the actual single-URL-button override this
                    screen sends when set (see the buttons payload just above) —
                    mirror that exact rule here instead of just showing the
                    template's first button, so the preview matches what's really
                    being sent whether that's the full template.buttons array or
                    the one override button. */}
                <LivePreviewPanel
                  isExpanded={isExpanded}
                  onToggle={() => setIsExpanded(prev => !prev)}
                  senderName={campaignData?.sender_name}
                  messageText={campaignData?.messages}
                  subject={campaignData?.subject}
                  previewText={campaignData?.email_header}
                  htmlContent={campaignData?.html_text}
                  costEstimate={costEstimate}
                  smsSegments={campaignData?.smsSegment || 1}
                  channel={campaignData?.campaign_channel || 'WhatsApp'}
                  mediaFile={campaignData?.mediaFile}
                  mediaUrl={campaignData?.mediaFileUrl || campaignData?.content?.header_value}
                  vars={campaignData?.content?.variables ?
                    Object.entries(campaignData.content.variables).reduce((acc, [key, value]) => {
                      acc[`variable_${key}` as keyof TemplateVariables] = value as string;
                      return acc;
                    }, {} as TemplateVariables)
                    : undefined
                  }
                  cta={campaignData?.link ? { label: "Learn More", url: campaignData.link } : undefined}
                  templateButtons={campaignData?.content?.buttons}
                  templateName={campaignData?.content?.template_name}
                  headerType={campaignData?.content?.header_type}
                  headerContent={campaignData?.content?.header_value}
                  rcsComponents={campaignData?.rcsComponents}
                />
              </div>
            </div>
          </div>

        </div>
      </div>

      <TestEmailModal
        isOpen={showTestEmail}
        onClose={() => setShowTestEmail(false)}
        onSend={handleSendTestEmail}
        maxRecipients={5}
      />

      <TestPhoneModal
        isOpen={showTestPhone}
        onClose={() => setShowTestPhone(false)}
        onSend={handleSendTestPhone}
        maxRecipients={5}
        channel={lowerChannel}
      />

      {/* Cost Breakdown Modal */}
      <CostBreakdownModal
        channel={campaignData?.campaign_channel}
        isOpen={showCostBreakdown}
        onClose={() => setShowCostBreakdown(false)}
        costEstimate={costEstimate}
      />

      <AddFundsModal
        isOpen={showAddFundsModal}
        onClose={() => setShowAddFundsModal(false)}
        userEmail={userEmail}
        onPaymentSuccess={() => {
          toast.success("Funds added — refreshing your wallet balance.");
          onWalletFunded?.();
        }}
      />
    </div>
  )
}

export default ScheduleLaunch;